import { useEffect, useState } from "react";
import Pod from "components/Pod/Pod";
import CourseItem from "./CourseItem";
import "./CourseListPane.scss";

const MODULE_ID_TEXT = "moduleId";
const COURSE_ID_TEXT = "courseId";

const parseIdFromHash = (hashValue, idText) => {
	const regex = new RegExp(`${idText}-(\\d+)`);
	const match = hashValue.match(regex);

	return match ? Number(match[1]) : null;
};

const CourseListPane = ({
	title = "Courses",
	courses = [],
	hash = "",
	scroll,
	previewMode = false,
	previewLockedLearningJourneys = false,
	supressExpand = false
}) => {
	const [expandedModuleId, setExpandedModuleId] = useState(null);
	const [scrollTarget, setScrollTarget] = useState(null);
	const [pendingCourseIdToExpand, setPendingCourseIdToExpand] = useState(null);

	useEffect(() => {
		if (!hash) {
			return;
		}

		const element = hash.startsWith("#") ? hash.slice(1) : hash;
		setScrollTarget(element);

		const courseId = parseIdFromHash(element, COURSE_ID_TEXT);
		const moduleId = parseIdFromHash(element, MODULE_ID_TEXT);

		const feedbackInformation = { courseId, moduleId };
		sessionStorage.setItem("feedbackInformation", JSON.stringify(feedbackInformation));

		/*
		 * Learner mode currently uses this one-shot flag when navigating via the
		 * right-hand ListNavigation component. Preview mode will not use that
		 * component, but we keep the behaviour optional so this pane can be reused
		 * safely in both modes.
		 */
		const shouldSuppressExpand =
			!previewMode && sessionStorage.getItem("suppressExpand") === "1";

		if (courseId !== null && !shouldSuppressExpand) {
			setPendingCourseIdToExpand(courseId);
		}

		if (shouldSuppressExpand) {
			sessionStorage.removeItem("suppressExpand");
		}
	}, [hash, previewMode]);

	useEffect(() => {
		if (pendingCourseIdToExpand == null || !Array.isArray(courses) || courses.length === 0) {
			return;
		}

		const targetCourse = courses.find((course) => {
			return (course.Id ?? course.id) === pendingCourseIdToExpand;
		});

		const firstModuleId = targetCourse?.SubContent?.[0]?.Id ?? null;

		if (firstModuleId != null) {
			setExpandedModuleId(firstModuleId);
		}

		setPendingCourseIdToExpand(null);
	}, [pendingCourseIdToExpand, courses]);

	useEffect(() => {
		if (!scrollTarget || !courses || courses.length === 0 || !scroll) {
			return;
		}

		scroll(scrollTarget);
		setScrollTarget(null);
	}, [scrollTarget, courses, scroll]);

	const handleExpandModule = (moduleId) => {
		setExpandedModuleId((prev) => {
			return prev === moduleId ? null : moduleId;
		});
	};

	return (
		<Pod title={title}>
			{courses.length > 0 && (
				<div className="course-list-pane">
					{courses.map((course) => {
						return (
							<CourseItem
								key={`${course.Id}-${course.OfferName ?? "preview"}`}
								course={course}
								expandedModuleId={expandedModuleId}
								onExpand={handleExpandModule}
								supressExpand={supressExpand}
								previewMode={previewMode}
								previewLockedLearningJourneys={previewLockedLearningJourneys}
							/>
						);
					})}
				</div>
			)}
		</Pod>
	);
};

export default CourseListPane;